import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Table, Collapse } from 'antd';
const { Panel } = Collapse;
import { RightOutlined } from '@ant-design/icons';

import './styles.less';

export const CookieTable = ( props ) => {
    const [ isExpanded, setExpanded ] = useState( true );
    const [ panelExpandedStates, setPanelExpandedStates ] = useState( {} );

    /**
     * Updates the state of the collapse panels to store whether or not
     * they are expanded. This only applies to the minimized view of the
     * table since the full table view doesn't use collapsible panels.
     * 
     * @param { string[] } expandedKeys - The array of panel keys that are now expanded.
     */
    const updatePanelStates = useCallback( ( expandedKeys ) => {
        if ( expandedKeys ) {
            let newPanelStates = { ...panelExpandedStates };

            // Set all to false (meaning collapsed).
            const stateKeys = Object.keys( newPanelStates );
            for ( let i = 0; i < stateKeys.length; i++ ) {
                newPanelStates[ stateKeys[ i ] ] = false;
            }

            // Set new expanded keys to true (meaning expanded).
            for ( let i = 0; i < expandedKeys.length; i++ ) {
                newPanelStates[ expandedKeys[ i ] ] = true;
            }

            setPanelExpandedStates( newPanelStates );
        }
    }, [ panelExpandedStates, setPanelExpandedStates ] );

    /**
     * Determines whether or not a collapse panel is expanded or not. This
     * only applies to the minimized view of the table since the full table
     * view doesn't use collapsible panels.
     * 
     * @param { string } key - The key of the panel to check.
     * @returns { boolean }
     */
    const isPanelExpanded = useCallback( ( key ) => {
        const panelState = panelExpandedStates[ key ];
        return ( panelState !== undefined ) && ( panelState );
    }, [ panelExpandedStates ] );

    return (
        <Collapse
            className="cookie-table"
            defaultActiveKey={ [ 'cookie-table-root' ] }
            ghost
            onChange={ ( activeKeys ) => { setExpanded( activeKeys.length > 0 ); } }
        >
            <Panel key="cookie-table-root" showArrow={ false } disabled={ !props.collapsible } header={
                <div className="cookie-table-header">
                    { props.title }
                    { props.collapsible &&
                        <RightOutlined className={ 'collapse-icon' + ( !isExpanded ? '' : ' expanded' ) } />
                    }
                </div>
            } >
                { /* The full-view table of cookie details. Visible if width >= 1092px. */ }
                <Table
                    bordered={ false }
                    className="cookie-table-content"
                    columns={ props.headers }
                    dataSource={ props.data }
                    pagination={ false }
                    rowKey={ () => ( 'table-row' ) }
                />

                { /* The minimized view of cookie details. Visible if width < 1092px. */ }
                <Collapse
                    className="cookie-table-min-content"
                    defaultActiveKey={ [] }
                    ghost
                    onChange={ ( activeKeys ) => { updatePanelStates( activeKeys ); } }
                >
                    {
                        // For each cookie entry in the table, create a new collapsible panel.
                        props.data.map( ( cookieDetails, index ) => {
                            return (
                                <Panel key={ `sub-panel-${ index }` } className="cookie-table-min-content-section" showArrow={ false } header={
                                    <div className="cookie-table-min-content-header">
                                        { cookieDetails.type }
                                        <RightOutlined className={ classnames(
                                            'collapse-icon',
                                            { 'expanded': isPanelExpanded( `sub-panel-${ index }` ) },
                                        ) } />
                                    </div>
                                } >
                                    {
                                        // Display the cookie column headers as sections. Don't display the
                                        // first header though since that is covered by the panel header.
                                        props.headers.map( ( column, index ) => {
                                            if ( index !== 0 ) {
                                                return (
                                                    <div key={ `section-${ index }` } className="cookie-table-min-content-section-block">
                                                        <div className="cookie-table-min-content-section-header">
                                                            { column.title }
                                                        </div>
                                                        <div className="cookie-table-min-content-section-body">
                                                            { column.render( cookieDetails[ column.dataIndex ] ) }
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                return <React.Fragment key={ `section-${ index }` }></React.Fragment>;
                                            }
                                        } )
                                    }
                                </Panel>
                            );
                        } )
                    }
                </Collapse>
            </Panel>
        </Collapse>
    );
};

CookieTable.defaultProps = {
    collapsible: false,
    title: undefined,
    headers: undefined,
};

CookieTable.propTypes = {
    collapsible: PropTypes.bool,
    title: PropTypes.oneOfType( [ PropTypes.string, PropTypes.node ] ).isRequired,
    headers: PropTypes.arrayOf( PropTypes.object ).isRequired,
};
